import { useRef, useState } from 'react';
import { geocodeAddress } from '../../lib/maps';

interface AddressInputProps {
  value: string;
  onChange: (address: string) => void;
  onGeocode: (lat: number, lng: number) => void;
  placeholder?: string;
  label?: string;
}

export function AddressInput({ value, onChange, onGeocode, placeholder, label }: AddressInputProps) {
  const [suggestions, setSuggestions] = useState<Array<{ lat: number; lng: number; displayName: string }>>([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  function handleChange(val: string) {
    onChange(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      const results = await geocodeAddress(val);
      setSuggestions(results);
      setOpen(results.length > 0);
    }, 400);
  }

  function selectSuggestion(s: { lat: number; lng: number; displayName: string }) {
    onChange(s.displayName);
    onGeocode(s.lat, s.lng);
    setOpen(false);
    setSuggestions([]);
  }

  return (
    <div ref={wrapRef} className="flex flex-col gap-1 relative">
      {label && <label className="text-xs font-medium text-app-muted">{label}</label>}
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        placeholder={placeholder}
        className="rounded-lg border border-app-border bg-app-card px-3 py-2 text-sm text-app-fg placeholder:text-app-muted focus:border-app-fg/30 focus:outline-none"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-app-card border border-app-border rounded-xl shadow-2xl overflow-hidden z-20 max-h-48 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectSuggestion(s)}
              className="flex flex-col w-full text-left px-3 py-2.5 hover:bg-black/5 transition-colors border-b border-app-border last:border-0 sk-hit"
            >
              <span className="text-xs text-app-fg truncate">{s.displayName}</span>
              <span className="text-caption2 text-app-muted">{s.lat.toFixed(5)}, {s.lng.toFixed(5)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
